import { Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../../../environments/environment';
import { BUILD_INFO } from '../../../../../environments/build-info';
import { OrderFlowSessionService } from '../order-flow-session.service';
import { CLIENT_CODE_VERSION } from '../order-flow-trace.service';
import {
  DecisaoCarga, MotivoBloqueio, SentryConfig, SentryRuntimeContext,
  decidirCarga, lerDoNotTrack, slugDoPath,
} from './sentry-config';
import {
  descartar, drenar, desinstalarCapturaPrecoce, enfileirar,
  observarChegada, totalDescartados,
} from './sentry-buffer';
import type { ContextoSentry, SentryRuntime } from './sentry-runtime';

/**
 * SentryService — orquestra o ciclo de vida do SDK sem nunca importá-lo.
 *
 * Divisão de trabalho:
 *   - `sentry-buffer`   captura antes de existir SDK (instalado no main.ts);
 *   - `sentry-config`   decide SE este device carrega (gates puros);
 *   - este serviço      decide QUANDO carrega e liga uma coisa na outra;
 *   - `sentry-runtime`  chunk lazy, o único que fala com o SDK.
 *
 * "Quando" é a parte não óbvia. Carregar no bootstrap devolveria ao caminho
 * crítico os ~90 KB que o épico de performance tirou. Carregar tarde demais
 * deixaria a página quebrada sem ninguém para contar. A regra: **idle depois do
 * `load`, mas imediatamente se um erro aparecer** — uma página que já quebrou
 * não tem mais orçamento de performance a proteger.
 */

/** Estados observáveis — o teste (e o `window.__mpSentry`) afirmam por aqui. */
export type EstadoSentry = 'inativo' | 'bloqueado' | 'agendado' | 'carregando' | 'ativo' | 'falhou';

/** Folga após o `load` antes de tentar o idle. Alinhado ao index.html (3s). */
const ATRASO_POS_LOAD_MS = 3000;
/** Teto do requestIdleCallback: sem isto, uma aba de fundo nunca carregaria. */
const IDLE_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class SentryService {
  private estado: EstadoSentry = 'inativo';
  private motivo: MotivoBloqueio = '';
  private runtime: SentryRuntime | null = null;
  private agendado = false;
  /**
   * Já existe um listener de `load` esperando para antecipar a carga?
   *
   * `antecipar()` é chamado a CADA erro que entra na fila, e o guard de
   * `agendado` só protege a carga em si — sem esta flag, 20 erros no boot
   * registrariam 20 listeners de `load` que fariam a mesma coisa.
   */
  private aguardandoLoad = false;

  constructor(
    private session: OrderFlowSessionService,
    private zone: NgZone,
    private injector: Injector,
  ) { }

  /**
   * Ponto de entrada (APP_INITIALIZER). Síncrono e barato de propósito: só
   * decide e agenda — não retorna promise, para não somar um milissegundo
   * sequer ao bootstrap.
   */
  boot(): void {
    try {
      const decisao = this.decidir();
      this.motivo = decisao.motivo;

      if (!decisao.carregar) {
        this.estado = 'bloqueado';
        // Gate reprovou: solta os listeners e a fila. Manter qualquer um dos
        // dois seria vazamento de memória em troca de nada.
        desinstalarCapturaPrecoce();
        descartar();
        observarChegada(null);
        this.expor();
        return;
      }

      this.estado = 'agendado';
      // Um erro chegando à fila antecipa a carga (ver comentário da classe).
      observarChegada(() => this.antecipar());
      this.agendarCarga();
      this.expor();
    } catch (e) {
      // Falha ao decidir nunca derruba o boot do app.
      this.estado = 'falhou';
    }
  }

  /**
   * Reporta um erro. Chamado pelo `OrderFlowErrorHandler` — o Sentry é um
   * SEGUNDO destino do mesmo erro, nunca um substituto do funil de saúde
   * (`ClientAppError` → alerta `js_error_spike`), que é o que dispara o Discord.
   *
   * Antes do SDK chegar, o erro vai para a fila; depois, direto ao SDK.
   */
  capture(erro: any, extra?: { [k: string]: any }): void {
    try {
      if (this.estado === 'bloqueado' || this.estado === 'falhou') return;
      if (this.runtime) {
        this.refrescarContexto();
        this.runtime.captureException(erro, extra);
        return;
      }
      enfileirar(erro, 'angular');
    } catch (e) {
      // Nunca propaga: quem chama é o ErrorHandler global.
    }
  }

  /** Estado atual — usado pelos testes e pela sonda `window.__mpSentry`. */
  get estadoAtual(): EstadoSentry { return this.estado; }
  get motivoBloqueio(): MotivoBloqueio { return this.motivo; }

  private decidir(): DecisaoCarga {
    return decidirCarga(this.config(), this.ambiente());
  }

  /**
   * Config e ambiente saem por métodos `protected` — e não lidos inline — pelo
   * mesmo motivo que o `recovery` do `OrderFlowErrorHandler` é público: são o
   * ponto de substituição do teste. Sem isso, todo spec veria apenas o
   * `environment` de desenvolvimento (DSN vazio) e a matriz de gates ficaria
   * sem cobertura — justamente a parte que, se errar, custa dinheiro.
   */
  protected config(): SentryConfig | undefined {
    return (environment as any).sentry as SentryConfig | undefined;
  }

  protected ambiente(): SentryRuntimeContext {
    const win: any = typeof window !== 'undefined' ? window : {};
    const nav: any = typeof navigator !== 'undefined' ? navigator : {};
    return {
      production: !!environment.production,
      hostname: (win.location && win.location.hostname) || '',
      isCypress: !!win.Cypress,
      doNotTrack: lerDoNotTrack(nav, win),
      deviceId: this.deviceIdSeguro(),
    };
  }

  /**
   * `deviceId` sem deixar o gate depender de storage. O serviço de sessão lê
   * localStorage; em modo privado/iframe isso pode lançar, e id vazio é tratado
   * pelo `decidirCarga` como fora da amostra — sem id estável não há coorte
   * estável (e é lá, não aqui, que o teste da regra vive).
   */
  private deviceIdSeguro(): string {
    try {
      return this.session.deviceId || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Agenda a carga para o primeiro idle após o `load` + folga.
   *
   * `runOutsideAngular` em tudo: um callback de idle dentro da zone dispara um
   * ciclo de change detection do app inteiro sem nenhuma mudança de estado —
   * anti-padrão explícito neste repo (`perf-antipatterns-evitar`).
   */
  private agendarCarga(): void {
    this.zone.runOutsideAngular(() => {
      const depois = () => setTimeout(() => this.emIdle(() => this.carregar()), ATRASO_POS_LOAD_MS);
      try {
        if (typeof document !== 'undefined' && document.readyState === 'complete') {
          depois();
        } else if (typeof window !== 'undefined') {
          window.addEventListener('load', depois, { once: true } as any);
        } else {
          depois();
        }
      } catch (e) {
        depois();
      }
    });
  }

  /**
   * Um erro já aconteceu: pula a folga e o idle.
   *
   * Mas ainda espera o `load` se a página não terminou de carregar. A diferença
   * importa: erro no boot é comum (um script de terceiro que falha basta), e
   * baixar 89 KB no meio do boot puniria o carregamento de todo device da
   * coorte por causa de um erro que nem é nosso. Depois do `load`, aí sim
   * imediato — a página já entregou o que tinha para entregar.
   */
  private antecipar(): void {
    if (this.agendado || this.aguardandoLoad || this.estado !== 'agendado') return;
    this.zone.runOutsideAngular(() => {
      try {
        if (typeof document === 'undefined' || document.readyState === 'complete') {
          this.carregar();
          return;
        }
        this.aguardandoLoad = true;
        window.addEventListener('load', () => this.carregar(), { once: true } as any);
      } catch (e) {
        this.carregar();
      }
    });
  }

  /** `protected` para o spec poder tornar o agendamento determinístico: o
   *  `jasmine.clock()` não controla `requestIdleCallback`. */
  protected emIdle(fn: () => void): void {
    const w: any = typeof window !== 'undefined' ? window : null;
    try {
      if (w && typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS });
        return;
      }
    } catch (e) { /* cai no setTimeout */ }
    setTimeout(fn, 0);
  }

  /**
   * O `import()` dinâmico — a fronteira do chunk. Tudo daqui para dentro é
   * lazy; tudo daqui para fora é eager. Guardado por `agendado` porque o idle e
   * a antecipação por erro podem disparar juntos.
   */
  protected carregar(): void {
    if (this.agendado) return;
    this.agendado = true;
    this.estado = 'carregando';
    this.expor();

    import('./sentry-runtime')
      .then((mod) => {
        this.runtime = mod.initSentryRuntime({
          // `this.config()`, e não o environment lido de novo: um segundo ponto
          // de acesso ao formato do environment sairia do alcance do teste.
          cfg: this.config() as SentryConfig,
          release: BUILD_INFO.release,
          dist: BUILD_INFO.dist,
          contexto: this.contextoAtual(),
          router: this.routerOuNulo(),
          pendentes: drenar(),
          descartados: totalDescartados(),
        });
        // A partir daqui quem escuta os erros globais é o SDK. Manter os nossos
        // listeners duplicaria cada erro (um pelo buffer, outro pelo Sentry).
        desinstalarCapturaPrecoce();
        observarChegada(null);
        this.estado = 'ativo';
        this.expor();
      })
      .catch(() => {
        // Chunk não carregou (rede, deploy no meio da sessão): a fila fica
        // presa e é descartada. Erro de telemetria não vira erro de app.
        this.estado = 'falhou';
        desinstalarCapturaPrecoce();
        descartar();
        observarChegada(null);
        this.expor();
      });
  }

  /**
   * Router só depois do SDK decidir carregar: pedi-lo no construtor criaria a
   * dependência no boot e ainda arriscaria uma referência circular de DI (o
   * `ErrorHandler` global, que consome este serviço, é o primeiro provider que
   * o Angular resolve).
   */
  private routerOuNulo(): Router | null {
    try {
      return this.injector.get(Router, null);
    } catch (e) {
      return null;
    }
  }

  private contextoAtual(): ContextoSentry {
    let restaurant = '';
    try {
      restaurant = slugDoPath(window.location.pathname);
    } catch (e) { /* fica vazio */ }
    return {
      deviceId: this.deviceIdSeguro(),
      attemptId: this.seguro(() => this.session.attemptId),
      restaurant,
      clientCodeVersion: CLIENT_CODE_VERSION,
      addressFlow: this.seguro(() => this.session.addressFlow),
    };
  }

  /**
   * Tags voláteis mudam DENTRO da sessão (a pessoa abre outra loja, começa um
   * pedido novo). Refrescar no momento da captura é o que faz o `attempt_id` do
   * evento apontar para o funil certo no Athena.
   */
  private refrescarContexto(): void {
    if (!this.runtime) return;
    try {
      this.runtime.setContexto({
        attemptId: this.seguro(() => this.session.attemptId),
        restaurant: slugDoPath(window.location.pathname),
        addressFlow: this.seguro(() => this.session.addressFlow),
      });
    } catch (e) { /* contexto é enfeite; o erro é o dado */ }
  }

  private seguro(fn: () => string): string {
    try {
      return fn() || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Sonda de diagnóstico. Sem ela, "o Sentry não está recebendo nada" vira uma
   * investigação; com ela, é `__mpSentry` no console e a resposta sai em um
   * segundo (`{estado: 'bloqueado', motivo: 'fora-da-amostra'}`).
   */
  private expor(): void {
    try {
      (window as any).__mpSentry = { estado: this.estado, motivo: this.motivo };
    } catch (e) { /* ignorado */ }
  }
}
